import { createAction, props } from '@ngrx/store';
import { EntityMap, EntityMapOne, Update } from '@ngrx/entity';
import { IDjangoPaginationData } from '@shared/ui-components/au-paginator/pagination-page.info';
import {
	ICmmsFilesQueryVariables,
	IFileEntity,
	IFilePayload,
} from '@app/pages/cmms/interfaces/files.interfaces';
import { AuGqlError } from '@app/shared';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import {
	DetectionTaskStatus,
	ICreateDetectionTaskVariables,
	IDetectionTask,
	IUpdateDetectionTaskVariables,
} from '@app/pages/cmms/interfaces/incident-detection.interfaces';
import type { LocationInput } from '@app/pages/cmms/interfaces/cmms-global.interfaces';

export const loadDefaultFiles = createAction('[FILES] Load files with default variables');

export const startLoadingFiles = createAction(
	'[FILES] Start loading file objects',
	props<{ variables: ICmmsFilesQueryVariables }>()
);

export const reloadFilesWithSavePagination = createAction(
	'[FILES] reload file objects with save pagination'
);

export const reloadFiles = createAction('[FILES] Start reloading files');

export const filesLoaded = createAction(
	'[FILES] Files loaded',
	props<{
		filesPagination: IDjangoPaginationData;
		files: IFileEntity[];
	}>()
);

export const storeFilesQueryVariables = createAction(
	'[FILES] Store files query variables',
	props<{ variables: ICmmsFilesQueryVariables }>()
);

export const dropFilesQueryVariables = createAction('[FILES] Drop files query variables');

export const filesFailedToLoad = createAction('[FILES] Files failed to load');

export const setFileForEditing = createAction(
	'[FILES] Add Files for editing',
	props<{
		fileEdit: IFileEntity;
	}>()
);

export const startEditFile = createAction(
	'[FILES] Start edit file',
	props<{
		file: Partial<IFilePayload>;
		hideSuccessNotification?: boolean;
		isAttaching?: boolean;
	}>()
);

export const editFileSuccess = createAction(
	'[FILES] Edit file success',
	props<{
		file: IFileEntity;
		isAttaching?: boolean;
	}>()
);

export const attachToFileSuccess = createAction('[FILES] Attach entity to file success');

export const setFiles = createAction(
	'[FILES] Set files',
	props<{ files: IFileEntity[] }>()
);
export const createFileSuccess = createAction(
	'[FILES] Create file success',
	props<{ file: IFileEntity }>()
);
export const createFileError = createAction(
	'[FILES] Create files error',
	props<{ error?: AuGqlError }>()
);
export const addFile = createAction('[FILES] Add file', props<{ file: IFileEntity }>());
export const setFile = createAction('[FILES] Set file', props<{ file: IFileEntity }>());
export const upsertFile = createAction(
	'[FILES] Upsert file',
	props<{ file: IFileEntity }>()
);
export const addFiles = createAction(
	'[FILES] Add files',
	props<{ files: IFileEntity[] }>()
);
export const upsertFiles = createAction(
	'[FILES] Upsert files',
	props<{ files: IFileEntity[] }>()
);
export const updateFile = createAction(
	'[FILES] Update file',
	props<{ update: Update<IFileEntity> }>()
);
export const updateFiles = createAction(
	'[FILES] Update files',
	props<{ updates: Update<IFileEntity>[] }>()
);
export const updateFileInsideFileEntity = createAction(
	'[FILES] Update files inside entity',
	props<{ newFile: File; fileEntity: IFileEntity }>()
);
export const updateFileSuccess = createAction(
	'[FILES] Update files success',
	props<{ workOrder: IFileEntity }>()
);
export const mapFile = createAction(
	'[FILES] Map file',
	props<{ entityMap: EntityMapOne<IFileEntity> }>()
);
export const mapFiles = createAction(
	'[FILES] Map files',
	props<{ entityMap: EntityMap<IFileEntity> }>()
);
export const startDeletingFile = createAction(
	'[FILES] Start deleting file',
	props<{ pk: string }>()
);
export const deletingFileSuccess = createAction(
	'[FILES] Deleting file success',
	props<{ pk: string }>()
);
export const startDeletingFiles = createAction(
	'[FILES] Start deleting files',
	props<{ pks: string[] }>()
);
export const deletingFilesSuccess = createAction(
	'[FILES] Deleting files success',
	props<{ pks: string[] }>()
);
export const deleteFile = createAction('[FILES] Delete file', props<{ pk: string }>());
export const deleteFiles = createAction(
	'[FILES] Delete files',
	props<{ pk: string[] }>()
);
export const replacingFileStarts = createAction('[FILES] Replacing file starts');

export const setFilesTableConfig = createAction(
	'[FILES] Set files table config',
	props<{
		isExportable: boolean;
		columns: ICMMSColumn[];
	}>()
);
export const createDetectionTask = createAction(
	'[Files] Create Detected Task',
	props<{
		variables: ICreateDetectionTaskVariables;
	}>()
);
export const setDetectionTask = createAction(
	'[Files] Set Detected Task Result',
	props<{
		result: IDetectionTask;
		detectionTaskStatus: DetectionTaskStatus;
	}>()
);
export const retrieveDetectionTask = createAction(
	'[Files] Retrive Detected Task',
	props<{
		fileId: string;
	}>()
);
export const updateDetectionTask = createAction(
	'[Files] Update Detected Task',
	props<{
		updateVariables: IUpdateDetectionTaskVariables;
	}>()
);
export const updateActiveTab = createAction(
	'[Files] Update Active Tab',
	props<{
		activeTab: 'view' | 'info';
		fileId: string;
	}>()
);

export const addFileExternalData = createAction(
	'[Files] Add files external data',
	(fileData = null) => ({ fileData })
);

export const openFileEntityWorkspaceFromOutside = createAction(
	'[Files] Open file entity workspace from outside',
	props<{ pk: string }>()
);

export const openNewFileEntityWorkspaceFromOutside = createAction(
	'[Files] Open new file entity workspace from outside',
	(fileData: { locations: LocationInput[] } = null) => ({ fileData })
);
